/**
 * PluginRegistryService.ts — self-registering Plugin Registry™ (Developer
 * Center backbone).
 *
 * ZERO manual registration: every component the platform already knows about
 * is discovered automatically —
 *   • Core engine registry  → core engines + modules (ids ending in "-module")
 *   • Fusion provider registry → API providers
 *   • Core background scheduler → schedulers
 * A new engine/module that calls registerEngine()/attachToCore() appears here
 * with no Developer Center changes.
 *
 * The registry is a pure OBSERVER of the platform: it never mutates engine or
 * module behaviour. Lifecycle controls delegate to Roamer Core™ (which itself
 * gates them behind authorize(), fail-closed). All metrics are recorded
 * observations; anything unmeasurable in a browser runtime is null.
 */

import { roamerCore } from '@/services/core';
import type { CoreEvent } from '@/services/core';
import { ALL_CORE_EVENT_NAMES } from '@/services/core/events/CoreEventBus';
import { eventContractRegistry } from '@/services/core/events/EventContractRegistry';
import { roamerDataFusionEngine } from '@/services/fusion';
import type {
  DependencyIssue,
  IntegrityAuditReport,
  PluginDeclaration,
  PluginHealthStatus,
  PluginLifecycleEvent,
  PluginLifecycleLogEntry,
  PluginRecord,
  PluginType,
} from './models';
import { runIntegrityAudit, validateDependencies } from './audit';
import type { AuditInputs } from './audit';

const LIFECYCLE_LOG_CAP = 500;
const SELF_ID = 'plugin-registry';

interface ObservedSource {
  eventNames: Set<string>;
  eventCount: number;
  lastEventAt: number;
}

interface SampleTally {
  total: number;
  running: number;
}

type CoreLike = typeof roamerCore;
type FusionLike = typeof roamerDataFusionEngine;

export class PluginRegistryService {
  private core: CoreLike | null = null;
  private fusion: FusionLike | null = null;
  private detachers: Array<() => void> = [];
  private attachedAt: number | null = null;

  private declarations = new Map<string, PluginDeclaration>();
  private observed = new Map<string, ObservedSource>();
  private samples = new Map<string, SampleTally>();
  private lifecycleLog: PluginLifecycleLogEntry[] = [];
  private changeListeners = new Set<() => void>();

  // ── Attachment ───────────────────────────────────────────────────────────────

  attach(core: CoreLike = roamerCore, fusion: FusionLike = roamerDataFusionEngine, now: number = Date.now()): void {
    if (this.core) return; // once only
    this.core = core;
    this.fusion = fusion;
    this.attachedAt = now;

    // Observe every Core event — attribution + heartbeat, never mutation.
    try {
      const offAny = core.bus.onAny((event: CoreEvent) => {
        try {
          this.recordObservation(event);
          if (event.name === 'EngineRegistered') {
            const payload = event.payload as { engineId?: string } | null;
            this.logLifecycle(payload?.engineId ?? 'unknown', 'register', 'Registered with Roamer Core™.', event.at);
          }
          if (event.name === 'EngineStatusChanged') {
            const payload = event.payload as { engineId?: string; status?: string } | null;
            this.logLifecycle(
              payload?.engineId ?? 'unknown',
              statusToLifecycle(payload?.status ?? ''),
              `Core status → ${payload?.status ?? 'unknown'}.`,
              event.at,
            );
          }
          this.notify();
        } catch {
          // Observer errors must never break the bus.
        }
      }, 'plugin-registry');
      this.detachers.push(offAny);
    } catch {
      // Core bus unavailable — registry still lists via polling snapshots.
    }

    this.logLifecycle(SELF_ID, 'start', 'Plugin Registry attached and observing.', now);
    this.sample(now);
  }

  detach(): void {
    for (const off of this.detachers) {
      try { off(); } catch { /* noop */ }
    }
    this.detachers = [];
    this.core = null;
    this.fusion = null;
  }

  /** Subscribe to registry changes (UI live updates). Returns unsubscribe. */
  onChange(listener: () => void): () => void {
    this.changeListeners.add(listener);
    return () => this.changeListeners.delete(listener);
  }

  // ── Optional supplemental declarations (never required for discovery) ────────

  declare(pluginId: string, declaration: PluginDeclaration, now: number = Date.now()): void {
    this.declarations.set(pluginId, { ...this.declarations.get(pluginId), ...declaration });
    this.logLifecycle(pluginId, 'update', 'Supplemental metadata declared.', now);
    this.notify();
  }

  // ── Discovery snapshot ───────────────────────────────────────────────────────

  list(now: number = Date.now()): PluginRecord[] {
    const records: PluginRecord[] = [];
    const env = detectEnvironment();

    // 1) Core engine registry → engines + modules.
    const diag = this.safeDiagnostics();
    for (const engine of diag?.engines ?? []) {
      const decl = this.declarations.get(engine.id);
      const obs = this.observed.get(engine.id);
      records.push(this.buildRecord({
        id: engine.id,
        name: engine.name,
        type: decl?.type ?? inferType(engine.id),
        version: engine.version,
        discoveredVia: 'core-registry',
        capabilities: engine.capabilities,
        status: engineStatusToHealth(engine.status, engine.enabled),
        enabled: engine.enabled,
        registeredAt: engine.registeredAt,
        controllable: true,
        env,
        decl,
        obs,
        now,
      }));
    }

    // 2) Fusion provider registry → API providers.
    const providerHealth = this.safeProviderHealth();
    for (const meta of this.safeProviders()) {
      const decl = this.declarations.get(meta.id);
      const health = providerHealth.get(meta.id);
      records.push(this.buildRecord({
        id: meta.id,
        name: meta.name ?? meta.id,
        type: 'apiProvider',
        version: 'live' in meta && meta.live === false ? 'placeholder' : '1.0',
        discoveredVia: 'fusion-registry',
        capabilities: [String(meta.domain)],
        status: providerStatus(health?.healthScore ?? null, health?.lastUsedAt ?? null),
        enabled: true,
        registeredAt: null,
        controllable: false,
        env,
        decl,
        obs: this.observed.get(meta.id),
        now,
        heartbeatOverride: health?.lastUsedAt ?? null,
      }));
    }

    // 3) Core background scheduler → schedulers.
    for (const job of diag?.jobs ?? []) {
      const j = job as { id: string; label?: string; runs?: number; failures?: number; lastRunAt?: number | null };
      records.push(this.buildRecord({
        id: `job:${j.id}`,
        name: j.label ?? j.id,
        type: 'scheduler',
        version: '1.0',
        discoveredVia: 'core-scheduler',
        capabilities: ['background-job'],
        status: (j.failures ?? 0) > 0 && (j.runs ?? 0) === 0 ? 'crashed' : 'running',
        enabled: true,
        registeredAt: null,
        controllable: false,
        env,
        decl: this.declarations.get(`job:${j.id}`),
        obs: this.observed.get(`job:${j.id}`),
        now,
        heartbeatOverride: j.lastRunAt ?? null,
      }));
    }

    // 4) The registry itself — a plugin like any other.
    records.push(this.buildRecord({
      id: SELF_ID,
      name: 'Plugin Registry™',
      type: 'service',
      version: '1.0',
      discoveredVia: 'self',
      capabilities: ['discovery', 'monitoring', 'audit'],
      status: this.core ? 'running' : 'offline',
      enabled: true,
      registeredAt: this.attachedAt,
      controllable: false,
      env,
      decl: this.declarations.get(SELF_ID),
      obs: this.observed.get(SELF_ID),
      now,
    }));

    return records;
  }

  /** Record a health sample for uptime tracking (recorded samples only). */
  sample(now: number = Date.now()): void {
    for (const record of this.list(now)) {
      const tally = this.samples.get(record.id) ?? { total: 0, running: 0 };
      tally.total += 1;
      if (record.status === 'running' || record.status === 'idle' || record.status === 'busy') tally.running += 1;
      this.samples.set(record.id, tally);
    }
    this.notify();
  }

  // ── Lifecycle controls (delegate to Core; authorize() gates them there) ──────

  async restartPlugin(id: string, now: number = Date.now()): Promise<boolean> {
    if (!this.core) return false;
    this.logLifecycle(id, 'restart', 'Restart requested via Developer Center.', now);
    const ok = await this.core.restartEngine(id, now);
    this.logLifecycle(id, ok ? 'recovery' : 'failure', ok ? 'Restart succeeded.' : 'Restart failed.', now);
    this.notify();
    return ok;
  }

  disablePlugin(id: string, now: number = Date.now()): boolean {
    if (!this.core) return false;
    const ok = this.core.disableEngine(id, now);
    if (ok) this.logLifecycle(id, 'pause', 'Disabled via Developer Center.', now);
    this.notify();
    return ok;
  }

  enablePlugin(id: string, now: number = Date.now()): boolean {
    if (!this.core) return false;
    const ok = this.core.enableEngine(id, now);
    if (ok) this.logLifecycle(id, 'resume', 'Enabled via Developer Center.', now);
    this.notify();
    return ok;
  }

  // ── Validation + audit ───────────────────────────────────────────────────────

  validate(now: number = Date.now()): DependencyIssue[] {
    return validateDependencies(this.list(now));
  }

  audit(now: number = Date.now()): IntegrityAuditReport {
    const stats = this.safeBusStats();
    const providerHealth: Record<string, number> = {};
    for (const [id, rec] of this.safeProviderHealth()) {
      // Only providers that have actually been used have a meaningful score.
      if (rec.lastUsedAt !== null) providerHealth[id] = rec.healthScore;
    }
    // Event Contract System™ inputs — best-effort, never blocks the audit.
    let contractFindings: string[] | undefined;
    let contractSnapshot: AuditInputs['contractSnapshot'];
    try {
      const snap = eventContractRegistry.getSnapshot();
      contractFindings = eventContractRegistry.verifyCoverage(ALL_CORE_EVENT_NAMES);
      contractSnapshot = {
        totalContracts: snap.totalContracts,
        knownEventCount: ALL_CORE_EVENT_NAMES.length,
        unknownEvents: snap.unknownEvents,
        failed: snap.failed,
        validated: snap.validated,
        duplicates: eventContractRegistry.getDuplicateRegistrations().length,
      };
    } catch { /* registry unavailable — audit proceeds without contract checks */ }
    return runIntegrityAudit({
      plugins: this.list(now),
      busPublished: stats?.published ?? 0,
      busDelivered: stats?.delivered ?? 0,
      providerHealth,
      contractFindings,
      contractSnapshot,
      now,
    });
  }

  // ── Introspection accessors for the Developer Center UI ─────────────────────

  getLifecycleLog(): PluginLifecycleLogEntry[] {
    return this.lifecycleLog.map((e) => ({ ...e }));
  }

  getObservedEvents(pluginId: string): string[] {
    return [...(this.observed.get(pluginId)?.eventNames ?? [])].sort();
  }

  // ── Internals ────────────────────────────────────────────────────────────────

  private buildRecord(input: {
    id: string;
    name: string;
    type: PluginType;
    version: string;
    discoveredVia: PluginRecord['discoveredVia'];
    capabilities: string[];
    status: PluginHealthStatus;
    enabled: boolean;
    registeredAt: number | null;
    controllable: boolean;
    env: string;
    decl: PluginDeclaration | undefined;
    obs: ObservedSource | undefined;
    now: number;
    heartbeatOverride?: number | null;
  }): PluginRecord {
    const tally = this.samples.get(input.id) ?? null;
    const observedNames = input.obs ? [...input.obs.eventNames] : [];
    const published = [...new Set([...(input.decl?.publishedEvents ?? []), ...observedNames])].sort();
    return {
      id: input.id,
      name: input.name,
      type: input.type,
      version: input.version,
      discoveredVia: input.discoveredVia,
      author: input.decl?.author ?? null,
      description: input.decl?.description ?? null,
      dependencies: input.decl?.dependencies ? [...input.decl.dependencies] : null,
      capabilities: [...input.capabilities],
      subscribedEvents: input.decl?.subscribedEvents ? [...input.decl.subscribedEvents] : null,
      publishedEvents: published,
      status: input.status,
      enabled: input.enabled,
      registeredAt: input.registeredAt,
      lastHeartbeatAt: input.heartbeatOverride ?? input.obs?.lastEventAt ?? null,
      observedEventCount: input.obs?.eventCount ?? 0,
      uptimePct: tally && tally.total > 0 ? Math.round((tally.running / tally.total) * 100) : null,
      healthSamples: tally?.total ?? 0,
      memoryBytes: null,
      cpuPct: null,
      environment: input.env,
      controllable: input.controllable,
    };
  }

  private recordObservation(event: CoreEvent): void {
    const source = this.observed.get(event.source) ?? { eventNames: new Set<string>(), eventCount: 0, lastEventAt: 0 };
    source.eventNames.add(event.name);
    source.eventCount += 1;
    source.lastEventAt = Math.max(source.lastEventAt, event.at);
    this.observed.set(event.source, source);
  }

  private logLifecycle(pluginId: string, event: PluginLifecycleEvent, detail: string | null, at: number): void {
    this.lifecycleLog.push({ at, pluginId, event, detail });
    if (this.lifecycleLog.length > LIFECYCLE_LOG_CAP) {
      this.lifecycleLog.splice(0, this.lifecycleLog.length - LIFECYCLE_LOG_CAP);
    }
  }

  private notify(): void {
    for (const listener of this.changeListeners) {
      try { listener(); } catch { /* listener errors never break the registry */ }
    }
  }

  private safeDiagnostics() {
    try { return this.core?.getDiagnostics() ?? null; } catch { return null; }
  }

  private safeBusStats() {
    try { return this.core?.bus.getStats() ?? null; } catch { return null; }
  }

  private safeProviders() {
    try { return this.fusion?.getProviderStatus() ?? []; } catch { return []; }
  }

  private safeProviderHealth(): Map<string, { healthScore: number; lastUsedAt: number | null }> {
    const map = new Map<string, { healthScore: number; lastUsedAt: number | null }>();
    try {
      for (const rec of this.fusion?.getProviderHealth() ?? []) {
        map.set(rec.providerId, { healthScore: rec.healthScore, lastUsedAt: rec.lastUsedAt });
      }
    } catch { /* fusion unavailable */ }
    return map;
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function inferType(id: string): PluginType {
  return id.endsWith('-module') ? 'module' : 'coreEngine';
}

function engineStatusToHealth(status: string, enabled: boolean): PluginHealthStatus {
  if (!enabled || status === 'disabled') return 'disabled';
  switch (status) {
    case 'running': return 'running';
    case 'registered': return 'idle';
    case 'failed': return 'crashed';
    case 'recovering': return 'restarting';
    default: return 'unknown';
  }
}

function providerStatus(healthScore: number | null, lastUsedAt: number | null): PluginHealthStatus {
  if (lastUsedAt === null) return 'idle'; // never exercised — honest, not "running"
  if (healthScore !== null && healthScore < 50) return 'crashed';
  return 'running';
}

function statusToLifecycle(status: string): PluginLifecycleEvent {
  switch (status) {
    case 'running': return 'running';
    case 'disabled': return 'pause';
    case 'failed': return 'failure';
    case 'unregistered': return 'remove';
    default: return 'update';
  }
}

function detectEnvironment(): string {
  try {
    return import.meta.env?.MODE ?? 'unknown';
  } catch {
    return 'unknown';
  }
}

export const pluginRegistry = new PluginRegistryService();
